/**
* Translation & legalization comparison cluster — /translation/compare/{slug}.
*
* ACCURACY POLICY (ตรวจทาน 2026-08-02)
* - ประเทศไทย "ไม่มี" ทะเบียนนักแปลสาบานตน (sworn translator) ตามกฎหมาย
* การรับรองคำแปลทำผ่าน (ก) ผู้แปล/บริษัทแปลรับรองตนเอง (ข) ทนายความผู้ทำ
* คำรับรองลายมือชื่อและเอกสาร ตามข้อบังคับสภาทนายความ หรือ (ค) นิติกรณ์
* รับรองคำแปลของกองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ
* - ค่าธรรมเนียมนิติกรณ์กรมการกงสุล: 200 บาท/ตราประทับ (ปกติ) และ
* 400 บาท/ตราประทับ (ด่วน) — consular.mfa.go.th ; ดู src/data/official-fees.ts
* - ไทยยังไม่ออก Apostille จนกว่าอนุสัญญาจะมีผลกับไทยวันที่ 28 ก.พ. 2027
* (ภาคยานุวัติ 30 มิ.ย. 2026) — hcch.net ; ดู src/data/legal-facts.ts
* - NAATI เป็นระบบรับรองนักแปลของออสเตรเลีย (naati.com.au) ไม่ใช่หน่วยงานไทย
* - ตัวเลขเวลา/ค่าใช้จ่ายทั้งหมดเขียนเป็น "ช่วงโดยประมาณ" การรับพิจารณาเป็น
* ดุลพินิจของหน่วยงานปลายทางเสมอ ห้ามรับประกันผลการพิจารณา
*/

export interface MltCompareRow {
  aspect: string;
  a: string;
  b: string;
}

export interface MltCompare {
  slug: string;
  title: string;
  description: string;
  h1: string;
  aLabel: string;
  bLabel: string;
  answer: string;
  rows: MltCompareRow[];
  chooseA: string[];
  chooseB: string[];
  faqs: Array<{ q: string; a: string }>;
  synonyms: string[];
}

const TAIL =
  "ข้อมูล ณ ส.ค. 2026 — ข้อกำหนดของหน่วยงานปลายทางเปลี่ยนแปลงได้ โปรดยืนยันรูปแบบการรับรองกับผู้รับเอกสารก่อนยื่นทุกครั้ง";

export const MLT_COMPARE: MltCompare[] = [
  {
    slug: "certified-translation-vs-notarised-translation",
    title: "Certified translation กับ Notarised translation ต่างกันอย่างไร",
    description:
      "เทียบทีละข้อระหว่างคำแปลรับรองโดยผู้แปล (certified) กับคำแปลที่ผ่านการรับรองลายมือชื่อโดยทนาย (notarised) ว่าใครรับรองอะไร ปลายทางไหนรับแบบไหน และต้องทำต่อกันหรือไม่",
    h1: "Certified translation vs Notarised translation",
    aLabel: "Certified translation (ผู้แปล/บริษัทแปลรับรอง)",
    bLabel: "Notarised translation (ทนายรับรองลายมือชื่อผู้แปล)",
    answer:
      "Certified translation คือคำแปลที่ผู้แปลหรือบริษัทแปลลงนามรับรองว่าแปลครบถ้วนตรงตามต้นฉบับ ส่วน Notarised translation คือการนำคำแปลนั้นไปให้ทนายความผู้ทำคำรับรองลายมือชื่อและเอกสารรับรอง “ลายมือชื่อของผู้แปล” อีกชั้นหนึ่ง ทนายไม่ได้รับรองว่าคำแปลถูกต้อง แต่รับรองว่าผู้แปลลงนามต่อหน้าจริง ทั้งสองแบบจึงเป็นคนละชั้น และหลายปลายทางต้องใช้ต่อกันตามลำดับ",
    rows: [
      { aspect: "ผู้รับรอง", a: "ผู้แปลหรือบริษัทแปล (คำรับรองตนเอง)", b: "ทนายความที่ขึ้นทะเบียนกับสภาทนายความแห่งประเทศไทย" },
      { aspect: "รับรองอะไร", a: "ความครบถ้วนตรงกับต้นฉบับของคำแปล", b: "ลายมือชื่อของผู้แปลที่ลงนามต่อหน้าทนาย ไม่ใช่เนื้อหาคำแปล" },
      { aspect: "เอกสารแนบทั่วไป", a: "Certificate of Accuracy + สำเนาต้นฉบับ", b: "คำแปล + คำรับรองผู้แปล + คำรับรองและตราประทับของทนาย" },
      { aspect: "ค่าธรรมเนียมราชการ", a: "ไม่มี", b: "ไม่มี (มีเฉพาะค่าวิชาชีพทนาย)" },
      { aspect: "ระยะเวลาโดยประมาณ", a: "1–3 วันทำการ ขึ้นกับจำนวนหน้าและคู่ภาษา", b: "เพิ่มจากงานแปลอีกประมาณ 1 วันทำการ ต้องนัดผู้แปลเซ็นต่อหน้าทนาย" },
      { aspect: "ใช้ต่อชั้นถัดไปได้ไหม", a: "โดยทั่วไปยังไม่พอสำหรับยื่นกงสุล/สถานทูต", b: "ใช้ต่อชั้นกรมการกงสุลและสถานทูตปลายทางได้ หากทนายจดแจ้งลายมือชื่อไว้" },
    ],
    chooseA: [
      "ปลายทางระบุเพียงว่าต้องเป็น certified translation หรือ translator's declaration",
      "ใช้ยื่นมหาวิทยาลัย นายจ้าง หรือหน่วยงานเอกชนในต่างประเทศ",
      "ต้องการงานเร็วและยังไม่ต้องผ่านหน่วยงานราชการ",
    ],
    chooseB: [
      "ปลายทางระบุคำว่า notarised / notarized translation",
      "ต้องยื่นต่อศาล หน่วยงานราชการ หรือสถานทูตในลำดับถัดไป",
      "ต้องการให้คำแปลเดินสายรับรองต่อไปยังกรมการกงสุลได้",
    ],
    faqs: [
      { q: "ทนายรับรองว่าคำแปลถูกต้องได้ไหม?", a: "ทนายรับรอง “ลายมือชื่อของผู้แปล” และข้อความคำรับรองที่ผู้แปลให้ไว้ ไม่ได้รับรองคุณภาพของการแปลในเชิงภาษา ความรับผิดเรื่องความถูกต้องของคำแปลอยู่ที่ผู้แปล " + TAIL },
      { q: "ต้องทำทั้งสองแบบเสมอไหม?", a: "ไม่เสมอไป ให้ยึดถ้อยคำที่ผู้รับเอกสารระบุเป็นลายลักษณ์อักษร หากระบุเพียง certified ก็ไม่จำเป็นต้องผ่านทนาย" },
      { q: "ผู้แปลต้องมาเซ็นเองไหม?", a: "ต้องมา เพราะการรับรองลายมือชื่อต้องเกิดต่อหน้าทนาย เราจึงจัดผู้แปลในทีมเข้าลงนามให้ในวันนัด" },
      { q: "ราคาต่างกันมากไหม?", a: "ค่าแปลเป็นตัวแปรหลักและคิดตามจำนวนหน้าและคู่ภาษา ส่วนขั้นรับรองโดยทนายเป็นค่าวิชาชีพเพิ่มต่อชุด เราแจ้งเป็นช่วงราคาหลังดูเอกสารจริงก่อนเริ่มงานเสมอ" },
      { q: "คำแปลภาษาที่สามใช้ได้ไหม?", a: "ได้ แต่ต้องตรวจก่อนว่าปลายทางรับคู่ภาษานั้น บางประเทศบังคับให้แปลเป็นภาษาราชการของตนเท่านั้น" },
      { q: "ต้นฉบับต้องเป็นตัวจริงไหม?", a: "ส่วนใหญ่ต้องแสดงต้นฉบับหรือสำเนาที่ได้รับรองถูกต้องจากหน่วยงานผู้ออกเอกสาร โดยเฉพาะเมื่อจะเดินสายต่อที่กรมการกงสุล" },
    ],
    synonyms: ["certified translation คือ", "notarised translation ต่างกัน", "แปลรับรองเอกสาร ทนาย", "คำรับรองคำแปล"],
  },
  {
    slug: "รับรองคำแปล-กงสุล-vs-ทนาย",
    title: "รับรองคำแปลที่กรมการกงสุล กับ รับรองโดยทนาย ต่างกันอย่างไร",
    description:
      "เทียบนิติกรณ์รับรองคำแปลของกรมการกงสุลกับคำรับรองของทนายความ ทั้งขอบเขตการรับรอง ค่าธรรมเนียม 200/400 บาท ระยะเวลา และลำดับที่ต้องทำก่อนหลัง",
    h1: "รับรองคำแปลกรมการกงสุล vs รับรองคำแปลโดยทนาย",
    aLabel: "นิติกรณ์รับรองคำแปล กรมการกงสุล",
    bLabel: "คำรับรองโดยทนาย (Notarial Services Attorney)",
    answer:
      "กองสัญชาติและนิติกรณ์ กรมการกงสุล รับรอง “ความถูกต้องของคำแปล” ระหว่างไทยกับอังกฤษเป็นหลัก และเป็นชั้นที่สถานทูตส่วนใหญ่ต้องการก่อน ส่วนทนายความรับรอง “ลายมือชื่อ” ของผู้ลงนามหรือผู้แปล ใช้กับเอกสารเอกชนและคู่ภาษาที่หลากหลายกว่า เมื่อเอกสารต้องใช้ต่อหน่วยงานรัฐต่างประเทศ ลำดับที่พบบ่อยคือทนายก่อน แล้วจึงกงสุล แล้วจึงสถานทูตปลายทาง",
    rows: [
      { aspect: "หน่วยงาน", a: "กองสัญชาติและนิติกรณ์ กรมการกงสุล กระทรวงการต่างประเทศ", b: "ทนายความที่ขึ้นทะเบียนกับสภาทนายความแห่งประเทศไทย" },
      { aspect: "ขอบเขตการรับรอง", a: "ความถูกต้องของคำแปลเทียบกับต้นฉบับ", b: "ลายมือชื่อผู้แปล/ผู้ลงนาม และสำเนาถูกต้อง" },
      { aspect: "คู่ภาษาที่รองรับ", a: "ไทย–อังกฤษเป็นหลัก ภาษาอื่นมีข้อจำกัด ควรตรวจสอบล่วงหน้า", b: "รองรับได้กว้างกว่า เพราะรับรองลายมือชื่อ ไม่ใช่เนื้อหาภาษา" },
      { aspect: "ค่าธรรมเนียมราชการ", a: "200 บาท/ตราประทับ (ปกติ) หรือ 400 บาท/ตราประทับ (ด่วน)", b: "ไม่มีค่าธรรมเนียมราชการ มีเฉพาะค่าวิชาชีพ" },
      { aspect: "ระยะเวลาโดยประมาณ", a: "แบบปกติราว 3 วันทำการ แบบด่วนรับได้ในวันเดียวกัน ขึ้นกับคิวของหน่วยงาน", b: "ทำได้ภายในวันเดียวหากเอกสารและผู้ลงนามพร้อม" },
      { aspect: "ยื่นแทนได้ไหม", a: "ยื่นแทนได้ตามแบบฟอร์มและหลักฐานที่กรมการกงสุลกำหนด", b: "ผู้ลงนามต้องเซ็นต่อหน้าทนายด้วยตนเอง" },
    ],
    chooseA: [
      "ปลายทางระบุว่าคำแปลต้องผ่านการรับรองจากกระทรวงการต่างประเทศ",
      "ต้องนำเอกสารไปรับรองต่อที่สถานทูตประเทศปลายทาง",
      "เป็นเอกสารราชการไทย เช่น สูติบัตร ทะเบียนบ้าน ทะเบียนสมรส",
    ],
    chooseB: [
      "เป็นเอกสารเอกชน เช่น สัญญา หนังสือมอบอำนาจ งบการเงิน",
      "คู่ภาษาไม่ใช่ไทย–อังกฤษ",
      "ปลายทางระบุเพียงว่าต้อง notarised โดยไม่ต้องผ่านกระทรวง",
    ],
    faqs: [
      { q: "ต้องผ่านทนายก่อนถึงจะยื่นกงสุลได้ไหม?", a: "ขึ้นกับชนิดเอกสาร เอกสารราชการไทยยื่นกงสุลได้โดยตรง ส่วนเอกสารเอกชนโดยทั่วไปต้องมีลายมือชื่อที่ตรวจสอบได้ก่อน จึงมักผ่านทนายที่จดแจ้งลายมือชื่อไว้กับกรมการกงสุล " + TAIL },
      { q: "ค่าธรรมเนียมนิติกรณ์เท่าไร?", a: "อัตราที่กรมการกงสุลประกาศคือ 200 บาทต่อตราประทับสำหรับบริการปกติ และ 400 บาทต่อตราประทับสำหรับบริการด่วน คิดต่อ “ตราประทับ” ไม่ใช่ต่อชุดเอกสาร จำนวนตราจึงขึ้นกับจำนวนแผ่นและรายการที่รับรอง" },
      { q: "กงสุลรับรองคำแปลภาษาญี่ปุ่นหรือจีนได้ไหม?", a: "การรับรองคำแปลของกรมการกงสุลเน้นคู่ภาษาไทย–อังกฤษ ภาษาอื่นมีเงื่อนไขเฉพาะและอาจต้องแปลเป็นอังกฤษก่อน เราจะตรวจกับหน่วยงานให้ก่อนวางแผนงานทุกครั้ง" },
      { q: "ถ้าคำแปลผิดจะเกิดอะไรขึ้น?", a: "เอกสารจะถูกตีกลับและต้องแก้แล้วยื่นใหม่ พร้อมเสียค่าธรรมเนียมรอบใหม่ เราจึงตรวจการสะกดชื่อกับหนังสือเดินทางและเอกสารทะเบียนราษฎรก่อนยื่นเสมอ" },
      { q: "หลังไทยใช้ Apostille แล้วยังต้องรับรองคำแปลไหม?", a: "อนุสัญญา Apostille จะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2027 และลดขั้นการรับรองที่สถานทูตสำหรับรัฐภาคี แต่ข้อกำหนดเรื่อง “ต้องแปลเป็นภาษาใด และใครรับรองคำแปล” ยังเป็นดุลพินิจของหน่วยงานปลายทาง" },
      { q: "ยื่นเองได้ไหม?", a: "ได้ครับ ทั้งยื่นด้วยตนเองที่กรมการกงสุลและทางไปรษณีย์ตามช่องทางที่ประกาศไว้ บริการของเราคือการเตรียมชุดเอกสารให้ถูกรูปแบบและเดินเรื่องแทนเพื่อลดรอบตีกลับ" },
    ],
    synonyms: ["รับรองคำแปล กงสุล", "นิติกรณ์ คำแปล", "ค่าธรรมเนียม 200 บาท กงสุล", "แปลเอกสารราชการ รับรอง"],
  },
  {
    slug: "naati-vs-คำแปลรับรองไทย",
    title: "คำแปล NAATI กับ คำแปลรับรองในไทย ใช้ต่างกันตรงไหน",
    description:
      "เทียบคำแปลโดยนักแปลที่ได้รับการรับรอง NAATI ของออสเตรเลีย กับคำแปลรับรองที่ทำในประเทศไทย ว่าปลายทางแบบไหนรับอะไร และเมื่อใดต้องใช้ทั้งสองอย่าง",
    h1: "คำแปล NAATI vs คำแปลรับรองในประเทศไทย",
    aLabel: "คำแปลโดยนักแปลรับรอง NAATI (ออสเตรเลีย)",
    bLabel: "คำแปลรับรองในไทย (ทนาย/กรมการกงสุล)",
    answer:
      "NAATI คือระบบรับรองคุณวุฒินักแปลและล่ามของประเทศออสเตรเลีย หน่วยงานออสเตรเลียจำนวนมาก เช่น กระทรวงมหาดไทยและหน่วยงานประเมินวุฒิ กำหนดให้ใช้คำแปลจากนักแปลที่ได้รับการรับรอง NAATI ส่วนคำแปลรับรองในไทยผ่านทนายหรือกรมการกงสุลใช้กับหน่วยงานไทยและประเทศปลายทางอื่น ๆ ทั้งสองระบบไม่ทดแทนกันโดยอัตโนมัติ ต้องดูว่าใครเป็นผู้รับเอกสาร",
    rows: [
      { aspect: "ระบบรับรอง", a: "NAATI — องค์กรรับรองนักแปล/ล่ามของออสเตรเลีย (naati.com.au)", b: "สภาทนายความแห่งประเทศไทย และกรมการกงสุล กระทรวงการต่างประเทศ" },
      { aspect: "ปลายทางที่มักกำหนด", a: "หน่วยงานรัฐและสถาบันการศึกษาในออสเตรเลีย", b: "หน่วยงานราชการไทย สถานทูตในไทย และประเทศปลายทางอื่น" },
      { aspect: "รูปแบบผลงาน", a: "คำแปลพร้อมตราประทับและหมายเลขผู้ปฏิบัติงาน NAATI", b: "คำแปลพร้อมคำรับรองผู้แปล คำรับรองทนาย หรือตรานิติกรณ์ของกรมการกงสุล" },
      { aspect: "ต้องผ่านกงสุลไหม", a: "โดยทั่วไปไม่ต้อง หากปลายทางระบุเพียงว่าต้องเป็นคำแปล NAATI", b: "ต้องผ่าน เมื่อปลายทางกำหนดสายรับรองถึงกระทรวงหรือสถานทูต" },
      { aspect: "ค่าธรรมเนียมราชการ", a: "ไม่มี เป็นค่าบริการนักแปลเท่านั้น", b: "200 บาท/ตราประทับ (ปกติ) หรือ 400 บาท/ตราประทับ (ด่วน) เฉพาะขั้นกรมการกงสุล" },
      { aspect: "ระยะเวลาโดยประมาณ", a: "1–5 วันทำการ ขึ้นกับคิวของนักแปลและจำนวนหน้า", b: "1 วันสำหรับขั้นทนาย และราว 3 วันทำการสำหรับขั้นกงสุลแบบปกติ" },
    ],
    chooseA: [
      "ยื่นวีซ่า ขอถิ่นที่อยู่ หรือประเมินวุฒิในประเทศออสเตรเลีย",
      "เอกสารระบุชัดว่าต้องเป็น NAATI certified translation",
      "ผู้รับเอกสารเป็นสถาบันการศึกษาหรือหน่วยงานวิชาชีพในออสเตรเลีย",
    ],
    chooseB: [
      "ใช้เอกสารกับหน่วยงานราชการไทยหรือสถานทูตต่างประเทศในไทย",
      "ปลายทางไม่ใช่ออสเตรเลียและไม่ได้ระบุ NAATI",
      "ต้องเดินสายรับรองต่อที่กรมการกงสุลและสถานทูต",
    ],
    faqs: [
      { q: "คำแปลไทยที่รับรองแล้วใช้ยื่นออสเตรเลียได้ไหม?", a: "ขึ้นกับหน่วยงานผู้รับ หลายหน่วยงานในออสเตรเลียระบุคำแปล NAATI ไว้ชัดเจน หากประกาศระบุเช่นนั้น คำแปลที่รับรองในไทยอาจไม่ถูกรับพิจารณา ควรตรวจข้อกำหนดเป็นลายลักษณ์อักษรก่อน " + TAIL },
      { q: "คำแปล NAATI ต้องรับรองที่กงสุลไทยอีกไหม?", a: "โดยทั่วไปไม่ต้อง เว้นแต่ต้องนำเอกสารกลับมาใช้กับหน่วยงานไทย หรือปลายทางกำหนดสายรับรองเพิ่มเติมไว้" },
      { q: "NAATI รับรองต้นฉบับด้วยหรือไม่?", a: "ไม่ นักแปล NAATI รับรองเฉพาะคำแปลของตน ความถูกต้องแท้จริงของต้นฉบับเป็นเรื่องของหน่วยงานผู้ออกเอกสาร" },
      { q: "ต้องส่งเอกสารตัวจริงไปออสเตรเลียไหม?", a: "ส่วนใหญ่ทำงานจากสำเนาสแกนคุณภาพสูงได้ แต่บางหน่วยงานขอดูต้นฉบับตอนยื่น จึงควรเก็บต้นฉบับไว้ให้ครบ" },
      { q: "ภาษาไทยเป็นคู่ภาษาที่ NAATI รับรองไหม?", a: "ใช่ คู่ภาษาไทย–อังกฤษเป็นคู่ภาษาที่มีผู้ได้รับการรับรองอยู่ เราจะยืนยันสถานะการรับรองของผู้ปฏิบัติงานก่อนรับงานทุกครั้ง" },
      { q: "หลังไทยใช้ Apostille แล้วเปลี่ยนอะไรไหม?", a: "Apostille จะมีผลกับไทยวันที่ 28 กุมภาพันธ์ 2027 และช่วยลดขั้นรับรองของ “ต้นฉบับ” ระหว่างรัฐภาคี แต่ข้อกำหนดเรื่องผู้แปลของออสเตรเลียยังเป็นคนละเรื่องและยังคงอ้างอิง NAATI" },
    ],
    synonyms: ["naati แปลเอกสาร", "naati กับ กงสุล", "แปลเอกสาร ยื่นออสเตรเลีย", "naati certified translation ไทย"],
  },
  {
    slug: "sworn-translator-vs-ไทย",
    title: "Sworn translator ในยุโรป กับ ระบบรับรองคำแปลของไทย ต่างกันอย่างไร",
    description:
      "หลายประเทศในยุโรปมีทะเบียนนักแปลสาบานตน แต่ประเทศไทยไม่มีทะเบียนดังกล่าว บทความเทียบให้เห็นว่าเอกสารไทยต้องเดินเส้นทางใดแทน เพื่อให้ปลายทางยอมรับ",
    h1: "Sworn translator (ยุโรป) vs การรับรองคำแปลแบบไทย",
    aLabel: "Sworn / assermenté / beeidigt translator",
    bLabel: "คำแปลรับรองแบบไทย (ผู้แปล + ทนาย + กรมการกงสุล)",
    answer:
      "ในหลายประเทศ เช่น ฝรั่งเศส เยอรมนี สเปน อิตาลี มีทะเบียนนักแปลที่สาบานตนต่อศาล คำแปลของบุคคลเหล่านี้มีผลตามกฎหมายในประเทศนั้นทันที ประเทศไทยไม่มีทะเบียนลักษณะนี้ เอกสารไทยจึงใช้เส้นทางแทนคือ ผู้แปลรับรองคำแปล แล้วให้ทนายรับรองลายมือชื่อ และ/หรือขอนิติกรณ์รับรองคำแปลจากกรมการกงสุล ก่อนนำไปรับรองที่สถานทูตปลายทาง",
    rows: [
      { aspect: "ฐานทางกฎหมาย", a: "ขึ้นทะเบียนและสาบานตนต่อศาลหรือหน่วยงานที่กฎหมายกำหนด", b: "ไม่มีทะเบียนนักแปลสาบานตนในไทย ใช้คำรับรองของผู้แปลประกอบการรับรองลายมือชื่อแทน" },
      { aspect: "ผู้รับรองความถูกต้องของคำแปล", a: "ตัวนักแปลสาบานตนเอง", b: "ผู้แปล และในบางกรณีกรมการกงสุลรับรองคำแปลอีกชั้น" },
      { aspect: "การยอมรับในประเทศปลายทาง", a: "ยอมรับทันทีในประเทศที่ขึ้นทะเบียน", b: "ยอมรับเมื่อผ่านสายรับรองครบตามที่ปลายทางกำหนด" },
      { aspect: "ค่าธรรมเนียมราชการ", a: "ตามอัตราของประเทศนั้น", b: "200 บาท/ตราประทับ (ปกติ) หรือ 400 บาท/ตราประทับ (ด่วน) เฉพาะขั้นกรมการกงสุล" },
      { aspect: "ระยะเวลาโดยประมาณ", a: "ขึ้นกับนักแปลและประเทศ", b: "รวมทุกขั้นโดยทั่วไปประมาณ 5–12 วันทำการ ขึ้นกับคิวของกรมการกงสุลและสถานทูต" },
      { aspect: "ความเสี่ยงที่พบบ่อย", a: "แปลนอกประเทศแล้วปลายทางไม่รับ เพราะนักแปลไม่ได้อยู่ในทะเบียน", b: "แปลถูกแต่เดินสายรับรองไม่ครบชั้น ทำให้ถูกตีกลับ" },
    ],
    chooseA: [
      "ปลายทางระบุว่าต้องเป็น traducteur assermenté / vereidigter Übersetzer / traductor jurado",
      "เอกสารใช้ยื่นต่อศาลหรือทะเบียนราษฎรของประเทศนั้นโดยตรง",
      "มีตัวแทนในประเทศปลายทางที่ดำเนินการแปลให้ได้",
    ],
    chooseB: [
      "เอกสารออกโดยหน่วยงานไทยและต้องรับรองจากต้นทางก่อนส่งออก",
      "ปลายทางยอมรับคำแปลที่ผ่านกระทรวงการต่างประเทศและสถานทูต",
      "ยังไม่ทราบข้อกำหนดแน่ชัด และต้องการเริ่มจากชั้นที่จำเป็นเสมอ",
    ],
    faqs: [
      { q: "ประเทศไทยมีนักแปลสาบานตนหรือไม่?", a: "ไม่มีทะเบียนนักแปลสาบานตนตามกฎหมายไทย การรับรองคำแปลจึงอาศัยคำรับรองของผู้แปลประกอบกับการรับรองลายมือชื่อโดยทนาย และนิติกรณ์รับรองคำแปลของกรมการกงสุล " + TAIL },
      { q: "แล้วเว็บที่โฆษณาว่ามีนักแปลสาบานตนไทยล่ะ?", a: "มักหมายถึงผู้แปลที่ทำคำรับรองประกอบคำแปลหรือผู้แปลที่ทำงานร่วมกับทนาย ไม่ใช่สถานะตามกฎหมายแบบยุโรป ควรถามให้ชัดว่าเอกสารจะได้ตราประทับจากใครบ้าง" },
      { q: "ต้องแปลที่ไทยหรือที่ประเทศปลายทาง?", a: "ถ้าปลายทางบังคับนักแปลในทะเบียนของเขา ควรแปลที่ประเทศนั้น ถ้าปลายทางยอมรับสายรับรองจากไทย การทำจากต้นทางมักเร็วกว่าและตรวจสอบต้นฉบับได้ง่ายกว่า" },
      { q: "แปลสองรอบเสียเงินซ้ำไหม?", a: "อาจเกิดขึ้นได้หากเลือกเส้นทางผิดตั้งแต่ต้น เราจึงขอข้อกำหนดเป็นลายลักษณ์อักษรจากผู้รับเอกสารก่อนเริ่มงานทุกครั้ง" },
      { q: "เอกสารต้องรับรองต้นฉบับก่อนแปลไหม?", a: "หลายปลายทางกำหนดให้รับรองต้นฉบับก่อน แล้วจึงแปลและรับรองคำแปล ลำดับนี้สำคัญเพราะการรับรองย้อนหลังมักทำไม่ได้" },
      { q: "Apostille ช่วยเรื่องคำแปลไหม?", a: "Apostille รับรองที่มาของเอกสารราชการ ไม่ได้รับรองคำแปล เมื่อมีผลกับไทยในวันที่ 28 กุมภาพันธ์ 2027 ก็ยังต้องจัดการเรื่องคำแปลตามข้อกำหนดของปลายทางแยกต่างหาก" },
    ],
    synonyms: ["sworn translator ไทย", "นักแปลสาบานตน", "traducteur assermenté thai", "beglaubigte übersetzung thailand"],
  },
  {
    slug: "แปลเป็นอังกฤษ-vs-แปลเป็นภาษาปลายทาง",
    title: "แปลเป็นภาษาอังกฤษ หรือแปลเป็นภาษาปลายทาง เลือกอย่างไร",
    description:
      "เอกสารไทยควรแปลเป็นอังกฤษเพื่อเดินสายรับรอง หรือแปลตรงเป็นภาษาปลายทาง บทความเทียบข้อดีข้อเสีย ค่าใช้จ่าย ระยะเวลา และข้อจำกัดของกรมการกงสุล",
    h1: "แปลเป็นอังกฤษ vs แปลเป็นภาษาปลายทาง",
    aLabel: "แปลไทย → อังกฤษ",
    bLabel: "แปลไทย → ภาษาปลายทางโดยตรง",
    answer:
      "การรับรองคำแปลของกรมการกงสุลเน้นคู่ภาษาไทย–อังกฤษ การแปลเป็นอังกฤษจึงเดินสายรับรองในไทยได้ราบรื่นที่สุด แต่หากหน่วยงานปลายทางกำหนดให้ยื่นเป็นภาษาราชการของเขา เช่น ญี่ปุ่น เกาหลี จีน เยอรมัน จะต้องแปลเป็นภาษานั้น และมักใช้เส้นทางรับรองลายมือชื่อผู้แปลโดยทนายแล้วต่อที่สถานทูตแทน ให้ยึดถ้อยคำในประกาศของผู้รับเอกสารเป็นหลัก",
    rows: [
      { aspect: "รับรองที่กรมการกงสุล", a: "ทำได้ตามปกติ เป็นคู่ภาษาหลักของนิติกรณ์รับรองคำแปล", b: "มีข้อจำกัด บางภาษาไม่รับ ต้องตรวจสอบล่วงหน้าเป็นรายกรณี" },
      { aspect: "การยอมรับที่สถานทูต", a: "สถานทูตส่วนใหญ่รับคำแปลอังกฤษ แต่บางแห่งบังคับภาษาของตน", b: "ตรงตามข้อกำหนดที่สุดเมื่อสถานทูตระบุภาษาไว้ชัด" },
      { aspect: "ค่าใช้จ่ายเปรียบเทียบ", a: "ต่ำกว่าโดยทั่วไป เพราะมีผู้แปลจำนวนมาก", b: "สูงกว่าในภาษาที่มีผู้แปลน้อย" },
      { aspect: "ระยะเวลาโดยประมาณ", a: "1–3 วันทำการต่อชุดเอกสารทั่วไป", b: "2–7 วันทำการ ขึ้นกับภาษาและปริมาณ" },
      { aspect: "ความเสี่ยงตีกลับ", a: "เกิดเมื่อปลายทางบังคับภาษาราชการของตน", b: "เกิดเมื่อคำแปลไม่ผ่านการรับรองชั้นที่ปลายทางต้องการ" },
      { aspect: "แนะนำเมื่อ", a: "ยังไม่ทราบปลายทางแน่ชัด หรือปลายทางรับอังกฤษ", b: "มีประกาศระบุภาษาของผู้รับเอกสารไว้ชัดเจน" },
    ],
    chooseA: [
      "ปลายทางรับเอกสารภาษาอังกฤษ เช่น ออสเตรเลีย สหราชอาณาจักร สิงคโปร์",
      "ต้องผ่านนิติกรณ์รับรองคำแปลของกรมการกงสุล",
      "ต้องการควบคุมค่าใช้จ่ายและเวลา",
    ],
    chooseB: [
      "สถานทูตหรือหน่วยงานปลายทางระบุภาษาราชการของตนไว้ชัด",
      "เอกสารใช้ยื่นต่อศาลหรือทะเบียนราษฎรของประเทศนั้น",
      "ผู้อ่านปลายทางไม่ใช้ภาษาอังกฤษในการพิจารณา",
    ],
    faqs: [
      { q: "แปลอังกฤษแล้วให้ปลายทางแปลต่อเองได้ไหม?", a: "หลายหน่วยงานไม่รับคำแปลต่อทอด เพราะตรวจสอบย้อนไปยังต้นฉบับไม่ได้ ควรแปลจากต้นฉบับภาษาไทยโดยตรงเสมอ " + TAIL },
      { q: "แปลสองภาษาในชุดเดียวกันได้ไหม?", a: "ได้ และบางเคสมีประโยชน์ เช่น อังกฤษสำหรับเดินสายรับรองในไทย และภาษาปลายทางสำหรับผู้พิจารณา แต่จะมีค่าใช้จ่ายและจำนวนตราประทับเพิ่มขึ้น" },
      { q: "ชื่อบุคคลควรสะกดอย่างไร?", a: "ให้ยึดการสะกดตามหนังสือเดินทางเป็นหลัก และใช้ให้ตรงกันทุกฉบับ การสะกดไม่ตรงกันเป็นสาเหตุการตีกลับที่พบบ่อยที่สุด" },
      { q: "ต้องแปลตราประทับและลายเซ็นด้วยไหม?", a: "ต้องระบุไว้ในคำแปล เช่น (ตราประทับ) (ลายมือชื่อ) พร้อมข้อความที่อ่านได้ เพราะผู้ตรวจใช้เทียบกับต้นฉบับทีละรายการ" },
      { q: "คู่ภาษาที่ให้บริการมีอะไรบ้าง?", a: "ทีมของเราทำงานในคู่ภาษาหลักที่ใช้กับงานเอกสารราชการ เช่น อังกฤษ จีน ญี่ปุ่น เกาหลี เวียดนาม อินโดนีเซีย และภาษายุโรปหลัก โปรดสอบถามคู่ภาษาที่ต้องการเพื่อยืนยันคิวผู้แปลก่อนเริ่มงาน" },
      { q: "ถ้าปลายทางเปลี่ยนข้อกำหนดกลางคัน?", a: "เกิดขึ้นได้ เราจึงเก็บไฟล์ต้นฉบับและคำแปลไว้ให้ เพื่อให้เพิ่มภาษาหรือเพิ่มชั้นรับรองได้โดยไม่ต้องเริ่มใหม่ทั้งชุด" },
    ],
    synonyms: ["แปลเอกสาร ภาษาอังกฤษ รับรอง", "แปลเอกสาร ญี่ปุ่น สถานทูต", "ควรแปลภาษาอะไร ยื่นวีซ่า", "คู่ภาษา แปลรับรอง"],
  },
  {
    slug: "แปลเอง-vs-ใช้ทีมแปลรับรอง",
    title: "แปลเอกสารเอง กับ ใช้ทีมแปลรับรอง ต่างกันอย่างไร",
    description:
      "เทียบข้อดีข้อเสียของการแปลเอกสารเองแล้วหาผู้รับรอง กับการใช้ทีมแปลที่เดินสายรับรองให้ครบ ทั้งเรื่องการยอมรับของหน่วยงาน ต้นทุนแฝง และรอบการตีกลับ",
    h1: "แปลเอกสารเอง vs ใช้ทีมแปลรับรองครบวงจร",
    aLabel: "แปลเองแล้วหาผู้รับรอง",
    bLabel: "ใช้ทีมแปลที่เดินสายรับรองให้",
    answer:
      "การแปลเองทำได้และประหยัดค่าแปล แต่หน่วยงานหลายแห่งไม่รับคำแปลที่เจ้าของเอกสารแปลเอง เพราะถือว่ามีส่วนได้เสีย อีกทั้งขั้นรับรองลายมือชื่อต้องให้ผู้แปลมาลงนามต่อหน้าทนาย และนิติกรณ์ของกรมการกงสุลจะตรวจคำแปลทีละรายการ ต้นทุนที่แท้จริงจึงอยู่ที่จำนวนรอบการตีกลับ ไม่ใช่ค่าแปลรอบแรก",
    rows: [
      { aspect: "การยอมรับของหน่วยงาน", a: "หลายแห่งไม่รับคำแปลของเจ้าของเอกสารเอง", b: "ผู้แปลเป็นบุคคลที่สามและลงนามรับรองได้" },
      { aspect: "ขั้นรับรองลายมือชื่อ", a: "ต้องหาผู้แปลที่ยินดีลงนามในคำแปลที่ตนไม่ได้แปล ซึ่งมักปฏิเสธ", b: "ผู้แปลในทีมลงนามต่อหน้าทนายได้ในวันนัด" },
      { aspect: "ความเสี่ยงตีกลับ", a: "สูง โดยเฉพาะการสะกดชื่อ วันที่ และคำเฉพาะทางราชการ", b: "ต่ำกว่า เพราะมีขั้นตรวจทานเทียบต้นฉบับก่อนยื่น" },
      { aspect: "ค่าใช้จ่าย", a: "ประหยัดค่าแปล แต่มีค่าธรรมเนียมซ้ำหากต้องยื่นใหม่", b: "ค่าบริการชัดเจนตั้งแต่ต้น ประเมินเป็นช่วงราคาหลังดูเอกสารจริง" },
      { aspect: "เวลาโดยรวม", a: "ไม่แน่นอน ขึ้นกับจำนวนรอบแก้", b: "วางแผนได้ตามคิวของหน่วยงาน" },
      { aspect: "เหมาะกับ", a: "เอกสารภายในที่ไม่ต้องรับรอง", b: "เอกสารที่ต้องยื่นราชการ สถานทูต หรือศาล" },
    ],
    chooseA: [
      "ใช้ภายในองค์กรหรือเพื่อความเข้าใจเท่านั้น",
      "ผู้รับเอกสารระบุว่ารับคำแปลอย่างไม่เป็นทางการ",
      "ยังอยู่ในขั้นเตรียมข้อมูลก่อนยื่นจริง",
    ],
    chooseB: [
      "ต้องยื่นหน่วยงานราชการ สถานทูต หรือใช้เป็นพยานหลักฐานในชั้นศาล",
      "ต้องมีตราประทับของทนายหรือกรมการกงสุล",
      "มีกำหนดเวลายื่นที่พลาดไม่ได้",
    ],
    faqs: [
      { q: "เจ้าของเอกสารแปลเองแล้วให้ทนายรับรองได้ไหม?", a: "ทนายรับรองลายมือชื่อได้ก็ต่อเมื่อมีผู้ลงนามที่รับผิดชอบคำแปล แต่หน่วยงานปลายทางหลายแห่งไม่รับคำแปลที่ผู้มีส่วนได้เสียแปลเอง จึงควรตรวจเงื่อนไขก่อน " + TAIL },
      { q: "ใช้เครื่องมือแปลอัตโนมัติได้ไหม?", a: "ใช้เป็นตัวช่วยร่างได้ แต่ต้องมีผู้แปลตรวจทานและรับผิดชอบ เพราะคำเฉพาะทางทะเบียนราษฎรและกฎหมายมักแปลผิดความหมาย" },
      { q: "ถ้าถูกตีกลับต้องเสียค่าธรรมเนียมใหม่ไหม?", a: "ค่าธรรมเนียมนิติกรณ์คิดตามตราประทับในการยื่นแต่ละครั้ง การยื่นใหม่จึงมีค่าธรรมเนียมใหม่ตามอัตราที่ประกาศไว้" },
      { q: "ต้องส่งต้นฉบับตัวจริงให้ทีมงานไหม?", a: "ขั้นแรกใช้ไฟล์สแกนคุณภาพสูงได้ แต่ขั้นยื่นกรมการกงสุลมักต้องใช้ต้นฉบับหรือสำเนาที่หน่วยงานผู้ออกรับรอง" },
      { q: "งานเร่งด่วนทำได้เร็วแค่ไหน?", a: "ขั้นแปลและรับรองโดยทนายทำได้ภายใน 1–2 วันทำการหากเอกสารพร้อม ส่วนขั้นกรมการกงสุลแบบด่วนรับได้ในวันเดียวกัน ทั้งหมดขึ้นกับคิวของหน่วยงาน" },
      { q: "มีการรับประกันว่าเอกสารจะผ่านไหม?", a: "ไม่มีผู้ให้บริการรายใดรับประกันผลได้ เพราะการรับพิจารณาเป็นดุลพินิจของหน่วยงานปลายทาง สิ่งที่เรารับผิดชอบคือความถูกต้องของคำแปลและความครบถ้วนของชั้นรับรองตามที่ปลายทางกำหนด" },
    ],
    synonyms: ["แปลเอกสารเอง ใช้ได้ไหม", "บริษัทแปลเอกสาร รับรอง", "แปลเอกสารราชการ ราคา", "แปลเอกสาร ตีกลับ"],
  },
];

export const MLT_COMPARE_BY_SLUG: Record<string, MltCompare> = Object.fromEntries(
  MLT_COMPARE.map((c) => [c.slug, c]),
);
